import { EventEmitter } from 'events';
import path from 'path';
import fs from 'fs-extra';
import chokidar from 'chokidar';
import { logger } from '../utils/logger';
import { TeamManager } from './team-manager';
import { WorkflowManager } from './workflow-manager';
import { Task, TaskState } from '../types';
import CryptoJS from 'crypto-js';

export interface SyncEvent {
  id: string;
  type: 'task_created' | 'task_updated' | 'task_completed' | 'task_assigned' | 'state_changed';
  userId: string;
  data: any;
  timestamp: string;
  checksum?: string;
}

export interface SyncOptions {
  projectRoot?: string;
  syncInterval?: number;
  enableConflictResolution?: boolean;
  enableEncryption?: boolean;
  encryptionKey?: string;
}

export interface ConflictResolution {
  strategy: 'latest-wins' | 'merge' | 'manual';
  resolver?: (local: any, remote: any) => any;
}

export class TeamSyncManager extends EventEmitter {
  private projectRoot: string;
  private syncDir: string;
  private teamManager: TeamManager;
  private workflowManager: WorkflowManager;
  private watcher: chokidar.FSWatcher | null = null;
  private syncInterval: NodeJS.Timer | null = null;
  private options: SyncOptions;
  private isSyncing = false;
  private syncQueue: SyncEvent[] = [];

  constructor(
    teamManager: TeamManager,
    workflowManager: WorkflowManager,
    options: SyncOptions = {}
  ) {
    super();
    this.teamManager = teamManager;
    this.workflowManager = workflowManager;
    this.options = {
      projectRoot: options.projectRoot || process.cwd(),
      syncInterval: options.syncInterval || 5000, // 5 seconds
      enableConflictResolution: options.enableConflictResolution ?? true,
      enableEncryption: options.enableEncryption ?? false,
      encryptionKey: options.encryptionKey
    };
    this.projectRoot = this.options.projectRoot;
    this.syncDir = path.join(this.projectRoot, '.workflow/sync');
  }

  async initialize(): Promise<void> {
    await fs.ensureDir(this.syncDir);
    await fs.ensureDir(path.join(this.syncDir, 'events'));
    await fs.ensureDir(path.join(this.syncDir, 'states'));
    
    // Start watching for changes
    await this.startWatching();
    
    // Start sync interval
    this.startSyncInterval();
    
    logger.info('Team sync manager initialized');
  }

  private async startWatching(): Promise<void> {
    const watchPaths = [
      path.join(this.projectRoot, '.workflow/tasks.json'),
      path.join(this.projectRoot, '.workflow/team/config.json')
    ];
    
    this.watcher = chokidar.watch(watchPaths, {
      ignored: /(^|[\/\\])\../, // ignore dotfiles
      persistent: true,
      ignoreInitial: true
    });
    
    this.watcher.on('change', async (filePath) => {
      if (!this.isSyncing) {
        await this.handleFileChange(filePath);
      }
    });
  }

  private async handleFileChange(filePath: string): Promise<void> {
    const currentUser = this.teamManager.getCurrentUser();
    if (!currentUser) return;
    
    try {
      if (filePath.includes('tasks.json')) {
        const taskState = await this.workflowManager.getTaskState();
        if (taskState) {
          await this.createSyncEvent({
            type: 'state_changed',
            userId: currentUser.id,
            data: taskState
          });
        }
      }
    } catch (error) {
      logger.error('Error handling file change:', error);
    }
  }

  private startSyncInterval(): void {
    this.syncInterval = setInterval(async () => {
      await this.processSyncQueue();
    }, this.options.syncInterval);
  }

  async stop(): Promise<void> {
    if (this.watcher) {
      await this.watcher.close();
    }
    
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    // Process remaining events
    await this.processSyncQueue();
  }

  async createSyncEvent(event: Omit<SyncEvent, 'id' | 'timestamp' | 'checksum'>): Promise<SyncEvent> {
    const syncEvent: SyncEvent = {
      ...event,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };
    
    // Add checksum for data integrity
    if (this.options.enableEncryption && this.options.encryptionKey) {
      const dataString = JSON.stringify(syncEvent.data);
      syncEvent.checksum = CryptoJS.SHA256(dataString).toString();
      syncEvent.data = this.encrypt(dataString);
    }
    
    this.syncQueue.push(syncEvent);
    
    // Emit event for real-time sync
    this.emit('syncEvent', syncEvent);
    
    return syncEvent;
  }

  private async processSyncQueue(): Promise<void> {
    if (this.isSyncing || this.syncQueue.length === 0) return;
    
    this.isSyncing = true;
    
    try {
      const events = [...this.syncQueue];
      this.syncQueue = [];
      
      for (const event of events) {
        await this.persistSyncEvent(event);
      }
      
      // Check for remote changes
      await this.pullRemoteChanges();
      
    } catch (error) {
      logger.error('Error processing sync queue:', error);
      // Re-add events to queue on failure
      this.syncQueue.unshift(...this.syncQueue);
    } finally {
      this.isSyncing = false;
    }
  }

  private async persistSyncEvent(event: SyncEvent): Promise<void> {
    const eventPath = path.join(this.syncDir, 'events', `${event.id}.json`);
    await fs.writeJson(eventPath, event, { spaces: 2 });
    
    // Also update the latest state file for quick access
    if (event.type === 'state_changed') {
      const statePath = path.join(this.syncDir, 'states', `${event.userId}-latest.json`);
      await fs.writeJson(statePath, {
        event,
        updatedAt: event.timestamp
      }, { spaces: 2 });
    }
  }

  private async pullRemoteChanges(): Promise<void> {
    const team = this.teamManager.getTeam();
    if (!team) return;
    
    const currentUser = this.teamManager.getCurrentUser();
    if (!currentUser) return;
    
    // Read other team members' states
    const statesDir = path.join(this.syncDir, 'states');
    const stateFiles = await fs.readdir(statesDir).catch(() => []);
    
    for (const file of stateFiles) {
      if (!file.endsWith('-latest.json') || file.startsWith(currentUser.id)) {
        continue;
      }
      
      try {
        const statePath = path.join(statesDir, file);
        const stateData = await fs.readJson(statePath);
        
        if (this.shouldApplyRemoteChange(stateData)) {
          await this.applyRemoteChange(stateData.event);
        }
      } catch (error) {
        logger.error(`Error reading state file ${file}:`, error);
      }
    }
  }

  private shouldApplyRemoteChange(stateData: any): boolean {
    // Check if the change is newer than our last sync
    const lastSyncPath = path.join(this.syncDir, '.last-sync');
    try {
      const lastSync = fs.readJsonSync(lastSyncPath);
      return new Date(stateData.updatedAt) > new Date(lastSync.timestamp);
    } catch {
      return true; // No last sync, apply all changes
    }
  }

  private async applyRemoteChange(event: SyncEvent): Promise<void> {
    try {
      let data = event.data;
      
      // Decrypt if needed
      if (this.options.enableEncryption && this.options.encryptionKey && event.checksum) {
        data = this.decrypt(event.data);
        
        // Verify checksum
        const checksum = CryptoJS.SHA256(data).toString();
        if (checksum !== event.checksum) {
          logger.error('Checksum mismatch, skipping event');
          return;
        }
        
        data = JSON.parse(data);
      }
      
      // Apply the change based on event type
      switch (event.type) {
        case 'task_assigned':
          await this.handleRemoteTaskAssignment(data);
          break;
        case 'task_updated':
          await this.handleRemoteTaskUpdate(data);
          break;
        case 'state_changed':
          await this.handleRemoteStateChange(data, event.userId);
          break;
      }
      
      // Update last sync timestamp
      await fs.writeJson(path.join(this.syncDir, '.last-sync'), {
        timestamp: new Date().toISOString()
      });
      
      // Emit event for UI updates
      this.emit('remoteChange', event);
      
    } catch (error) {
      logger.error('Error applying remote change:', error);
    }
  }

  private async handleRemoteTaskAssignment(data: any): Promise<void> {
    // Update local task state with assignment
    const taskState = await this.workflowManager.getTaskState();
    if (taskState && taskState.current && taskState.current.id === data.taskId) {
      taskState.current.assignee = data.assigneeId;
      await this.workflowManager.updateTaskState(taskState);
    }
  }

  private async handleRemoteTaskUpdate(data: any): Promise<void> {
    // Merge remote task updates
    if (this.options.enableConflictResolution) {
      await this.resolveTaskConflict(data);
    }
  }

  private async handleRemoteStateChange(data: TaskState, userId: string): Promise<void> {
    // Handle state changes from other team members
    const member = this.teamManager.getMemberById(userId);
    if (!member) return;
    
    // Log team member activity
    await this.teamManager.logActivity({
      type: 'status_changed',
      actorId: userId,
      data: {
        task: data.current?.task,
        phase: data.current?.phase
      }
    });
  }

  private async resolveTaskConflict(remoteData: any): Promise<void> {
    const localState = await this.workflowManager.getTaskState();
    if (!localState) return;
    
    // Simple latest-wins strategy for now
    const remoteTime = new Date(remoteData.updatedAt || 0).getTime();
    const localTime = new Date(localState.current?.startTime || 0).getTime();
    
    if (remoteTime > localTime) {
      await this.workflowManager.updateTaskState(remoteData);
    }
  }

  // Encryption helpers
  private encrypt(data: string): string {
    if (!this.options.encryptionKey) return data;
    return CryptoJS.AES.encrypt(data, this.options.encryptionKey).toString();
  }

  private decrypt(data: string): string {
    if (!this.options.encryptionKey) return data;
    const bytes = CryptoJS.AES.decrypt(data, this.options.encryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  // Public API for team collaboration
  async broadcastTaskAssignment(taskId: string, assigneeId: string): Promise<void> {
    const currentUser = this.teamManager.getCurrentUser();
    if (!currentUser) return;
    
    await this.createSyncEvent({
      type: 'task_assigned',
      userId: currentUser.id,
      data: {
        taskId,
        assigneeId,
        assignedBy: currentUser.id,
        assignedAt: new Date().toISOString()
      }
    });
  }

  async getTeamTaskStates(): Promise<Map<string, TaskState>> {
    const states = new Map<string, TaskState>();
    const team = this.teamManager.getTeam();
    if (!team) return states;
    
    const statesDir = path.join(this.syncDir, 'states');
    const stateFiles = await fs.readdir(statesDir).catch(() => []);
    
    for (const file of stateFiles) {
      if (!file.endsWith('-latest.json')) continue;
      
      try {
        const userId = file.replace('-latest.json', '');
        const member = this.teamManager.getMemberById(userId);
        if (!member) continue;
        
        const statePath = path.join(statesDir, file);
        const stateData = await fs.readJson(statePath);
        
        if (stateData.event?.data) {
          states.set(userId, stateData.event.data);
        }
      } catch (error) {
        logger.error(`Error reading team state ${file}:`, error);
      }
    }
    
    return states;
  }
}